import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Button, Card, Form, Input, Space, notification, Spin, Modal } from 'antd';

import * as actions from '../store/actions';
import * as sharedActions from '../../../shared/store/actions';

import { fileTypeTypes } from '../../../shared/constants/enums';

import MappingModal from '../mapping-modal/index';
import MergeResultsModal from '../merge-results-modal';
import MappingTree from '../mapping-tree';

import '../styles.less';
import { isArray, isEmpty } from 'lodash';

const { confirm } = Modal;

const filledReverseMapping = ({ rootContainer, formId, redirectUrl }) => {
  const dispatch = useDispatch();

  const [pdfForm] = Form.useForm();

  const [showMergeResultsModal, setShowMergeResultsModal] = useState(false);
  const [showMappedTree, setShowMappedTree] = useState(false);
  const [targetFocus, setTargetFocus] = useState();
  const [initiatingDone, setInitiatingDone] = useState(false);

  const mergeFromFormLoading = useSelector((state) => state.reverseMapping.mergeFromFormLoading);
  const mappings = useSelector((state) => state.reverseMapping.mappings);
  const mappingsLoading = useSelector((state) => state.reverseMapping.mappingsLoading);
  const formName = useSelector((state) => state.shared.formName);
  const formNameLoading = useSelector((state) => state.shared.formNameLoading);
  const mappingObjectNames = useSelector((state) => state.reverseMapping.mappingObjectNames);
  const reverseMappingForm = useSelector((state) => state.reverseMapping.reverseMappingForm);
  const reverseMappingFormLoading = useSelector((state) => state.reverseMapping.reverseMappingFormLoading);

  useEffect(() => {
    let promiseArray = [
      dispatch(actions.getReverseMappingForm({ formId })),
      dispatch(actions.getMappings({ formId, fileType: fileTypeTypes.PdfFormReverseMappingFilled })),
      dispatch(sharedActions.getFormName(formId)),
      dispatch(sharedActions.getMaxFileUploadSize()),
      dispatch(actions.getMappingObjectNames()),
    ];

    Promise.all(promiseArray).then(() => {
      setInitiatingDone(true);
    });
  }, []);

  useEffect(() => {
    showMappedTree ? (document.body.style.overflow = 'hidden') : (document.body.style.overflow = 'unset');
  }, [showMappedTree]);

  useEffect(() => {
    pdfForm.setFieldsValue({ formName });
  }, [formName]);

  useEffect(() => {
    setColorOfMappedFormFields(mappings);
  }, [initiatingDone, mappings]);

  const onMergeFromForm = () => {
    dispatch(actions.mergeFromForm({ formId })).then((results) => {
      if (results && isArray(results) && results.length > 0) {
        setShowMergeResultsModal(true);
      } else {
        afterMerge();
      }
    });
  };

  const afterMerge = () => {
    notify('Your form has been merge successfully');

    setShowMergeResultsModal(false);

    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  const afterNotFullyMerge = () => {
    notifyWarning('Your form has been merge not fully');

    setShowMergeResultsModal(false);

    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  const notify = (description) => {
    notification.success({
      message: 'Success',
      top: 55,
      duration: 10,
      getContainer: rootContainer,
      description: description,
    });
  };

  const notifyWarning = (description) => {
    notification.warning({
      message: 'Warning',
      top: 55,
      duration: 10,
      getContainer: rootContainer,
      description: description,
    });
  };

  const onPdfLoadedEvent = () => {
    setColorOfMappedFormFields(mappings);
  };

  const setColorOfMappedFormFields = (newUserMappingsItems) => {
    if (isArray(newUserMappingsItems)) {
      newUserMappingsItems.forEach((x) => {
        if (x && !isEmpty(x.formFieldName)) {
          const elements = document.getElementsByName(x.formFieldName);

          if (elements) {
            const element = elements[0];

            if (element && element.style) {
              element.style.backgroundColor = '#e3c1c1';
            }
          }
        }
      });
    }
  };

  const onShowMappedTreeClick = () => {
    setShowMappedTree(true);
  };

  const afterCloseModal = () => {
    if (targetFocus) {
      targetFocus.focus();

      targetFocus.classList.add('blink-background');

      const timer = setInterval(() => {
        clearInterval(timer);
        targetFocus.classList.remove('blink-background');
      }, 1000);

      setTargetFocus();
    }
  };

  const onCancel = () => {
    setShowMappedTree(false);
  };

  const onCancelClick = () => {
    confirm({
      getContainer: rootContainer,
      title: 'All changes will be lost. Are you sure you want to Cancel?',
      okText: 'Yes',
      cancelText: 'No',
      onOk() {
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      },
    });
  };

  return (
    <div id="mergeReverseMapping">
      <Card title="PDF Form" size="small">
        <Form name="PDF_Form" form={pdfForm} size="small">
          <Spin spinning={formNameLoading}>
            <Form.Item label="Name" name="formName" required>
              <Input disabled={true} />
            </Form.Item>
          </Spin>
        </Form>
      </Card>

      <Card title="PDF Data Mapping" size="small" className="card-data-mapping">
        <p>
          Press on the merge button and the information filled in the form will be inserted or edited in the system based on the mappings you set. You
          can see the mappings by clicking the fields.
        </p>

        <div className="show-mapped-tree-button">
          <Button onClick={onShowMappedTreeClick}>Show mapped tree</Button>
        </div>

        <Modal
          className="mapping-modal"
          title="Mapped Fields To Objects"
          open={showMappedTree}
          getContainer={rootContainer}
          centered
          width={800}
          onCancel={onCancel}
          onOk={onCancel}
          afterClose={afterCloseModal}
          footer={[
            <Button key="submit" type="primary" onClick={onCancel}>
              Ok
            </Button>,
          ]}
        >
          <MappingTree
            originalMappingObjects={mappingObjectNames}
            userMappingObjects={mappings}
            setTargetFocus={setTargetFocus}
            setShowMappingModal={setShowMappedTree}
          />
        </Modal>

        <MappingModal
          rootContainer={rootContainer}
          userMappingObjects={mappings}
          setNewUserMappings={undefined} // only view pdf
          loading={mappingsLoading}
          isPreview={true}
          fileEditorLoading={!reverseMappingForm || reverseMappingFormLoading || mergeFromFormLoading || !initiatingDone}
          fileBytes={reverseMappingForm}
          onPdfLoadedEvent={onPdfLoadedEvent}
        />
      </Card>

      <div className="action-buttons">
        <Space>
          <Button onClick={onCancelClick}>Cancel</Button>
          <Button type="primary" onClick={onMergeFromForm} loading={mergeFromFormLoading || !initiatingDone}>
            Merge from Form
          </Button>
        </Space>
      </div>

      <MergeResultsModal rootContainer={rootContainer} showModal={showMergeResultsModal} okCallback={afterNotFullyMerge} />
    </div>
  );
};

export default filledReverseMapping;
